import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, TextInput, SelectInput } from "@/components/FormKit";
import { tasksApi, apiErrorMessage, isSuppressionOverride } from "@/api/client";
import { OverrideNotice } from "@/components/forms/OverrideNotice";
import { toast } from "sonner";

export function TaskFormDialog({ open, onOpenChange, orgId, onSaved }) {
  const [form, setForm] = useState({ title: "", due_at: "", priority: "normal", notes: "" });
  const [override, setOverride] = useState("");
  const [needsOverride, setNeedsOverride] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setForm({ title: "", due_at: "", priority: "normal", notes: "" }); setOverride(""); setNeedsOverride(false); setError(""); }
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.due_at) { setError("Title and due date are required"); return; }
    setSaving(true); setError("");
    const payload = {
      title: form.title, organisation_id: orgId, priority: form.priority,
      due_at: new Date(form.due_at).toISOString(), notes: form.notes || null,
    };
    if (needsOverride) payload.override_reason = override;
    try {
      const res = await tasksApi.create(payload);
      toast.success("Follow-up added");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      if (isSuppressionOverride(err)) { setNeedsOverride(true); setError(""); }
      else setError(apiErrorMessage(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="task-form-dialog">
        <DialogHeader><DialogTitle className="font-heading">Add follow-up</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Title"><TextInput testid="task-title" value={form.title} onChange={set("title")} placeholder="Call back about menu" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Due"><TextInput testid="task-due" type="datetime-local" value={form.due_at} onChange={set("due_at")} /></Field>
            <Field label="Priority">
              <SelectInput testid="task-priority" value={form.priority} onChange={set("priority")}>
                {["low", "normal", "high"].map((p) => <option key={p} value={p}>{p}</option>)}
              </SelectInput>
            </Field>
          </div>
          <Field label="Notes"><TextInput testid="task-notes" value={form.notes} onChange={set("notes")} /></Field>
          {needsOverride && <OverrideNotice value={override} onChange={setOverride} entity="follow-up" />}
          {error && <p data-testid="task-form-error" className="text-sm font-medium text-rose-600">{error}</p>}
          <button type="submit" disabled={saving || (needsOverride && !override.trim())} data-testid="task-form-submit"
            className="min-h-[48px] w-full rounded-lg bg-teal-700 font-semibold text-white hover:bg-teal-800 disabled:opacity-60">
            {saving ? "Saving…" : needsOverride ? "Proceed with override" : "Add follow-up"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
